import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiFetch from "../../utlis/apiFetch";
import { forceLogout } from "../../utlis/forceLogout";

const handleAuthError = (error, rejectWithValue) => {
  if (error.message === "Your session has expired") forceLogout();
  return rejectWithValue(error.message);
};

const initialState = {
  checklists: [],
  loading: false,
  error: null,
  success: false,
};

export const fetchChecklists = createAsyncThunk(
  "checklist/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const response = await apiFetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/checklist/getAllCheckLists`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = response._data ?? await response.json();
      if (!response.ok)
        throw new Error(data.message || "Failed to fetch checklists");

      return data;
    } catch (error) {
      return handleAuthError(error, rejectWithValue);
    }
  }
);

export const createChecklist = createAsyncThunk(
  "checklist/create",
  async (formData, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const response = await apiFetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/checklist/createChecklist`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      const data = response._data ?? await response.json();
      if (!response.ok)
        throw new Error(data.message || "Failed to create checklist");

      return data;
    } catch (error) {
      console.error("Error uploading checklist:", error);
      return handleAuthError(error, rejectWithValue);
    }
  }
);

export const updateChecklist = createAsyncThunk(
  "checklist/update",
  async ({ formData }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const response = await apiFetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/checklist/updateCheckList`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      const data = response._data ?? await response.json();
      if (!response.ok)
        throw new Error(data.message || "Failed to update checklist");

      return data;
    } catch (error) {
      return handleAuthError(error, rejectWithValue);
    }
  }
);

const checklistSlice = createSlice({
  name: "checklist",
  initialState,
  reducers: {
    resetSuccess: (state) => {
      state.success = false;
    },

    logState: (state) => {
      console.log("Current checklist state:", {
        loading: state.loading,
        success: state.success,
        error: state.error,
        checklistCount: state.checklists.length,
      });
    },
  },
  extraReducers: (builder) => {
    builder

      .addCase(fetchChecklists.pending, (state) => {
        state.loading = true;
        state.success = false;
      })
      .addCase(fetchChecklists.fulfilled, (state, action) => {
        state.loading = false;
        state.checklists = action.payload.body;
        state.error = null;
      })
      .addCase(fetchChecklists.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })

      .addCase(createChecklist.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(createChecklist.fulfilled, (state, action) => {
        state.loading = false;
        state.checklists.push(action.payload.body);
        state.error = null;
        state.success = true;
      })
      .addCase(createChecklist.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })

      .addCase(updateChecklist.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(updateChecklist.fulfilled, (state, action) => {
        state.loading = false;
        const updatedChecklist = action.payload.body;
        state.checklists = state.checklists.map((checklist) => {
          if (
            checklist.country === updatedChecklist.country &&
            checklist.visaCategory === updatedChecklist.visaCategory
          ) {
            return updatedChecklist;
          }
          return checklist;
        });

        state.error = null;
        state.success = true;
        console.log("Update successful, setting success state to true");
      })
      .addCase(updateChecklist.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      });
  },
});

export const { resetSuccess, logState } = checklistSlice.actions;
export default checklistSlice.reducer;
